import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { Button } from "#/components/common/Button";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useApprovals } from "../hooks";

function formatDate(value?: string | null) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
}

export default function ApprovalInboxPage() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const approvalsQuery = useApprovals(page, size);

  if (approvalsQuery.isLoading) {
    return <PageLoader label="Loading approvals..." />;
  }

  if (approvalsQuery.isError) {
    return (
      <ErrorState
        title="Failed to load approvals"
        message={
          approvalsQuery.error instanceof Error
            ? approvalsQuery.error.message
            : "Could not load approvals."
        }
      />
    );
  }

  const data = approvalsQuery.data;
  const items = data?.content ?? [];

  if (!data || items.length === 0) {
    return (
      <EmptyState
        title="No approval items"
        description="Items that require your action will appear here."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Approval inbox</h1>
        <p className="text-sm text-slate-500">
          Review pending items assigned to you.
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900">
                  {item.title}
                </h2>

                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                  {item.module ? <span>Module: {item.module}</span> : null}
                  <span>Status: {item.status}</span>
                  <span>Submitted by: {item.submittedBy || "-"}</span>
                  <span>Submitted at: {formatDate(item.submittedAt)}</span>
                </div>
              </div>

              {item.actionUrl ? (
                <Link to={item.actionUrl}>
                  <Button variant="secondary">Review</Button>
                </Link>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <DataPagination
        page={data.number}
        totalPages={data.totalPages}
        totalElements={data.totalElements}
        size={data.size}
        onPageChange={setPage}
        onSizeChange={(nextSize) => {
          setSize(nextSize);
          setPage(0);
        }}
      />
    </div>
  );
}